import { h } from 'preact';
import { forwardRef, ForwardedRef } from 'preact/compat';

import classNames from 'classnames';
import * as keyboardEventHelpers from 'widget_main/globals/keyboardEvents';
import { useTouchMove } from 'widget_main/globals/hooks';
import styles from './textArea.scss';

interface TextAreaProps {
  ariaLabel: string;
  onChange: (e: Event) => unknown;
  onClick?: (e: Event) => unknown;
  autoFocus?: boolean;
  maxLength?: number;
  id?: string;
  rows?: number;
  value?: string;
  disabled?: boolean;
  onEnter?: (e: KeyboardEvent) => unknown;
  className?: string;
  placeholder?: string;
}

const TextArea = forwardRef(
  (
    {
      ariaLabel,
      onChange,
      onClick,
      autoFocus = false,
      onEnter,
      id,
      className,
      placeholder,
      value,
      disabled,
      maxLength = 10240,
      rows,
    }: TextAreaProps,
    ref: ForwardedRef<HTMLTextAreaElement>,
  ) => {
    const touchTarget =
      ref != null && typeof ref !== 'function' ? ref.current : undefined;
    useTouchMove(touchTarget ?? undefined);

    const handleKeyPress = (e: KeyboardEvent) => {
      if (keyboardEventHelpers.isEnterKey(e)) {
        e.preventDefault();
        e.stopPropagation();

        if (onEnter) {
          onEnter(e);
        }
      }
    };

    return (
      <textarea
        id={id}
        ref={ref}
        tabIndex={0}
        aria-label={ariaLabel}
        class={classNames(styles.textArea, className)}
        placeholder={placeholder}
        // eslint-disable-next-line jsx-a11y/no-autofocus
        autoFocus={autoFocus}
        rows={rows}
        onChange={onChange}
        onClick={onClick}
        onKeyPress={handleKeyPress}
        value={value}
        disabled={disabled}
        maxLength={maxLength}
      />
    );
  },
);

TextArea.displayName = 'TextArea';

export default TextArea;
